import React from 'react';
import moment from 'moment';
import { NextPageContext } from 'next';
import Head from 'next/head';
import Layout from '../../components/layout';
import SubpageHero from '../../components/subpageHero';
import BannerAd from '../../components/ad/banner';
import SideAds, { BlockAdsOne, BlockAdsTwo } from '../../components/sideAds';
import ArticleSummaryExcerpt from '../../components/articleSummary/excerpt';
import Pagination from '../../components/pagination';
import SectionHeader from '../../components/sectionHeader';
import SEO from '../../components/seo';
import PostsWithAdverts from '../../containers/postsWithAdverts';
import pageWrapper from '../../containers/pageWrapper';
import getTitleFromPage from '../../utils/data/getTitleFromPage';
import { fetchPage, fetchPosts } from '../../utils/data/fetch';

interface Props {
  menus: WordpressHeaderMenu[];
  posts: WordpressPage[];
  page: WordpressPage;
  total: number;
  pageNo?: number;
  error?: any;
  adverts: KeyedAdverts;
}

const News = ({ posts, menus, pageNo, total, page, adverts }: Props) => {
  const title = getTitleFromPage('News');
  const showLatestHeading = pageNo === undefined || pageNo === 1;

  return (
    <>
      <Head>
        <title key='title'>{title}</title>
        <SEO yoastMeta={page.yoast_meta} />
        <meta property='og:image' content={page.acf.page_image} />
      </Head>
      <Layout menus={menus}>
        <SubpageHero image={page.acf.page_image} title='News' />
        <BannerAd className='my-4' advert={adverts['banner-top']} />
        <div className='grid grid-cols-2 md:grid-cols-4 gap-0 sm:gap-4 mb-4'>
          <div className='col-span-4 md:col-span-3 mb-0 md:mb-4'>
            {showLatestHeading && (
              <SectionHeader className='mb-4'>Latest News</SectionHeader>
            )}
            <PostsWithAdverts
              posts={posts}
              adverts={adverts}
              render={(n: WordpressPage) => {
                return (
                  <ArticleSummaryExcerpt
                    key={n.id}
                    link={`/news/${n.slug}`}
                    className='mb-4'
                    title={n.title.rendered}
                    subTitle={moment(n.date).format('Do MMMM yyyy')}
                    image={n.acf.thumbnail}
                    excerpt={n.acf.excerpt}
                  />
                );
              }}
            />
          </div>
          <SideAds adverts={adverts} />
        </div>
        <BlockAdsOne adverts={adverts} />
        <BlockAdsTwo adverts={adverts} />
        <Pagination
          className='my-4'
          currentPage={pageNo || 1}
          totalPosts={total}
        />
      </Layout>
    </>
  );
};

News.getInitialProps = async ({ query }: NextPageContext) => {
  const pageNoParam = Array.isArray(query.page) ? query.page[0] : query.page;
  const pageNo = pageNoParam ? parseInt(pageNoParam) : undefined;

  const page = await fetchPage('news');
  const { posts, total } = await fetchPosts('news', pageNo);
  return { page, posts, total, pageNo };
};

export default pageWrapper(News);
